// ============================================
// useSEO — dynamic <title>, <meta>, <link canonical>, JSON-LD injection
// ============================================
import { useEffect } from 'react'

interface SEOOptions {
  title: string
  description: string
  canonical: string
  keywords?: string
  ogImage?: string
  ogImageAlt?: string
  ogType?: 'website' | 'profile'
  locale?: string
  alternateLocale?: string
  alternates?: Array<{ hrefLang: string; href: string }>
  jsonLd?: Record<string, unknown>
}

const SCRIPT_ID = 'nw-json-ld'

export function useSEO({
  title,
  description,
  canonical,
  keywords,
  ogImage,
  ogImageAlt,
  ogType = 'website',
  locale,
  alternateLocale,
  alternates,
  jsonLd,
}: SEOOptions) {
  useEffect(() => {
    // ── title ──
    document.title = title

    // ── meta helpers ──
    function setMeta(attr: 'name' | 'property', key: string, content: string) {
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.content = content
    }

    function removeMeta(attr: 'name' | 'property', key: string) {
      document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)?.remove()
    }

    setMeta('name', 'description', description)
    if (keywords) setMeta('name', 'keywords', keywords)
    else removeMeta('name', 'keywords')
    setMeta('property', 'og:site_name', 'Leka AI')
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', canonical)
    setMeta('property', 'og:type', ogType)
    if (locale) setMeta('property', 'og:locale', locale)
    else removeMeta('property', 'og:locale')
    if (alternateLocale) setMeta('property', 'og:locale:alternate', alternateLocale)
    else removeMeta('property', 'og:locale:alternate')
    if (ogImage) {
      setMeta('property', 'og:image', ogImage)
      setMeta('property', 'og:image:width', '1200')
      setMeta('property', 'og:image:height', '630')
      setMeta('property', 'og:image:type', 'image/jpeg')
      if (ogImageAlt) setMeta('property', 'og:image:alt', ogImageAlt)
      setMeta('name', 'twitter:image', ogImage)
      if (ogImageAlt) setMeta('name', 'twitter:image:alt', ogImageAlt)
    }
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:site', '@lekaai')
    setMeta('name', 'twitter:title', title)
    setMeta('name', 'twitter:description', description)

    // ── canonical ──
    let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonicalEl) {
      canonicalEl = document.createElement('link')
      canonicalEl.rel = 'canonical'
      document.head.appendChild(canonicalEl)
    }
    canonicalEl.href = canonical

    document.documentElement.lang = locale?.startsWith('ja') ? 'ja' : 'en'

    document.querySelectorAll('link[data-nw-hreflang="1"]').forEach((el) => el.remove())
    for (const alternate of alternates ?? []) {
      const link = document.createElement('link')
      link.rel = 'alternate'
      link.hreflang = alternate.hrefLang
      link.href = alternate.href
      link.setAttribute('data-nw-hreflang', '1')
      document.head.appendChild(link)
    }

    // ── JSON-LD ──
    let ldEl = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (jsonLd) {
      if (!ldEl) {
        ldEl = document.createElement('script')
        ldEl.id = SCRIPT_ID
        ldEl.type = 'application/ld+json'
        document.head.appendChild(ldEl)
      }
      ldEl.textContent = JSON.stringify(jsonLd)
    } else {
      ldEl?.remove()
    }

    // cleanup: restore defaults on unmount
    return () => {
      document.title = 'Leka AI'
    }
  }, [title, description, canonical, keywords, ogImage, ogImageAlt, ogType, locale, alternateLocale, JSON.stringify(alternates ?? []), jsonLd])
}
